/**
 * Map **object-track** polyline layer (ADR-041 D-4; REQ-UI-167 / -168 /
 * -173 / -181) — a line through each live object track's positioned members,
 * ending at its head glyph. Non-visual React component (like
 * :func:`TrackTailLayer`) mounted by :file:`Map.tsx`.
 *
 * Responsibilities:
 *   1. render the shared object-track store's presented tracks (two or more
 *      members) as lines, updating the MapLibre source in place;
 *   2. gate on the Profile's ``map.visible_layers.tracks`` (REQ-UI-173):
 *      hidden ⇒ the layer is detached — the head glyphs stay (the detection
 *      layer owns them);
 *   3. fade a quiet track by head age over the live window (REQ-UI-168) —
 *      re-converting on a slow tick so a silent track keeps fading between
 *      store updates.
 *
 * # This component only READS the object-track store
 *
 * The store is fed by :func:`useMapSseBridges` (the ``detection`` SSE frames,
 * via the detections bridge), :func:`useObjectTracksFeed` (the viewport
 * backfill mounted in :file:`Map.tsx`) and :func:`useRecentTracksFeed` (the
 * Tracks panel's global poll). It mounts no feed and no bridge.
 *
 * # Selection (REQ-UI-171 / -172)
 *
 *   - **Originates:** a line click writes ``{kind: "track", id}`` to BOTH
 *     slots (``selectOnMap`` — ring + Entity Details together). The head
 *     glyph paints over its own line and the detection layer's click handler
 *     registers after this one, so clicking the head selects the *detection*
 *     (which then expands its track in the Tracks panel — REQ-UI-180).
 *   - **Consumes:** the ``mapSelected`` track paints in the brand action
 *     accent, wider, and — unless the Profile turns it off — under a dashed
 *     overlay that makes it read as hatched (REQ-UI-167 / -200; the accent and
 *     the hatching were both specified in 2026-09-30 and neither was built,
 *     which is why a selected line among neighbours was hard to pick out).
 *     All three ride the ``selected`` feature property. A plain selection
 *     never moves the camera.
 *   - **Focus:** the Tracks-panel row's "Select on Map" raises the map-focus
 *     signal; this layer answers a ``track`` target with ``fitBounds`` over
 *     the track's extent (padding 64 px, max zoom 16, 200 ms) — the first
 *     extent-shaped focus in the product — easing to the point instead when
 *     the track has exactly one positioned member.
 *
 * Cross-references:
 *   - REQ-UI-167 / -168 / -173 / -181; ADR-041 D-4 / D-7.
 *   - map/object-track-layer-impl.ts (the primitives).
 *   - map/track-tail-layer.tsx (the sibling this mirrors).
 */
import { useEffect, useState } from "react";
import type { LngLatBoundsLike, Map as MapLibreMap, MapMouseEvent } from "maplibre-gl";
import { useLiveWindowMs } from "@/chrome/use-deployment-config";
import { useLiveObjectTracks, useObjectTrackStore } from "@/object-tracks/object-track-store";
import { useMapSelectedIdForKind, useSelectionStore } from "@/selection/selection-store";
import { useMapFocusSignal } from "@/selection/map-focus-store";
import {
  OBJECT_TRACK_AGE_TICK_MS,
  OBJECT_TRACK_LAYER_ID,
  TRACK_FOCUS_DURATION_MS,
  TRACK_FOCUS_MAX_ZOOM,
  TRACK_FOCUS_PADDING_PX,
  attachObjectTrackLayer,
  detachObjectTrackLayer,
  nearestTrackAtPoint,
  objectTracksToFeatureCollection,
  trackFocusBounds,
  trackHitBox,
} from "@/map/object-track-layer-impl";
import { GLYPH_PRECEDENCE_LAYERS } from "@/map/background-deselect";
import { isMapUsable } from "@/map/map-ready";
import { useTracksLayerVisible } from "@/map/use-tracks-layer-visible";
import { useSelectedTrackDash } from "@/map/use-selected-track-dash";

/** Props for :func:`ObjectTrackLayer`. */
export interface ObjectTrackLayerProps {
  /** Live MapLibre map instance (mounted after the map's ``load``). */
  map: MapLibreMap;
  /**
   * The Profile's ``map.detection.single_color`` — the line paints in the
   * head glyph's colour so the two read as one object (REQ-UI-046).
   */
  singleColor?: string;
}

export function ObjectTrackLayer({ map, singleColor }: ObjectTrackLayerProps): null {
  const tracks = useLiveObjectTracks();
  const visible = useTracksLayerVisible();
  const liveWindowMs = useLiveWindowMs();
  // The MAP slot (the two-slot bus): a Tracks-list left-click inspects without
  // widening the line; only a line click / "Select on Map" sets it.
  const selectedTrackId = useMapSelectedIdForKind("track");
  // REQ-UI-200: the patterned overlay on the selected line — whether it
  // draws and which pattern. The width is REQ-UI-167's and is not
  // configurable; the overlay is, because it is a density trade-off the
  // deployment owns.
  const { enabled: selectedDash, pattern: selectedDashPattern } = useSelectedTrackDash();
  const selectOnMap = useSelectionStore((s) => s.selectOnMap);
  const focusSignal = useMapFocusSignal();

  // The slow tick that keeps a silent track fading (REQ-UI-168).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), OBJECT_TRACK_AGE_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  // Detach once on unmount (or a rare ``map`` change).
  useEffect(() => {
    return () => {
      detachObjectTrackLayer(map);
    };
  }, [map]);

  // Render / visibility gate. Idempotent attach updates the source in place;
  // hidden ⇒ detached (the head glyphs are the detection layer's and stay).
  useEffect(() => {
    if (!visible) {
      detachObjectTrackLayer(map);
      return;
    }
    attachObjectTrackLayer(
      map,
      objectTracksToFeatureCollection(tracks, {
        now: Date.now(),
        liveWindowMs,
        selectedTrackId,
      }),
      singleColor,
      { selectedDash, selectedDashPattern, selectedTrackId },
    );
    // ``tick`` is a deliberate dependency: it re-runs the conversion so the
    // age fraction advances with no store change.
  }, [
    map,
    tracks,
    visible,
    liveWindowMs,
    singleColor,
    selectedTrackId,
    selectedDash,
    selectedDashPattern,
    tick,
  ]);

  // Originate selection: a line click (REQ-UI-171 / -199).
  //
  // Registered **map-wide**, not layer-scoped. MapLibre's layer-scoped handler
  // hit-tests the exact cursor point against the rendered geometry, and the
  // line renders 2 px wide — measured on the hermetic rig 2026-10-09, a click
  // selected the track at 0, 1 and 2 px off the line and nothing beyond, which
  // is the "tracks are difficult to select" the operator reported. The handler
  // now queries a 24 px corridor (``trackHitBox``) and takes the line passing
  // closest to the click (``nearestTrackAtPoint``), because a corridor that
  // wide catches a neighbour wherever tracks run close together.
  //
  // Two things the corridor makes the handler's own business, where the
  // layer-scoped version got them for free:
  //
  //   - **The layer may be absent.** ``queryRenderedFeatures`` throws on a
  //     layer id the style does not hold, and the layer is detached whenever
  //     the Tracks toggle is off (REQ-UI-173). MapLibre simply never called a
  //     layer-scoped listener in that case.
  //   - **Glyphs win.** A head glyph sits on its own line and is smaller than
  //     the corridor, so the corridor would otherwise steal clicks aimed at
  //     it. Precedence is now explicit — if any glyph, marker, label or
  //     cluster is under the exact cursor point, the click is theirs — rather
  //     than resting on the order MapLibre happens to invoke two handlers in,
  //     which is what the previous comment here relied on.
  //
  // The same corridor is used by ``isBackgroundClick`` (one exported
  // constant): a click inside it but off the rendered line must not read as
  // background, or the map-wide deselect handler would clear this selection on
  // the same click — the fourth edition of that race.
  useEffect(() => {
    const onClick = (event: MapMouseEvent): void => {
      if (!isMapUsable(map) || map.getLayer(OBJECT_TRACK_LAYER_ID) === undefined) {
        return;
      }
      const glyphLayers = GLYPH_PRECEDENCE_LAYERS.filter((id) => map.getLayer(id) !== undefined);
      if (
        glyphLayers.length > 0 &&
        map.queryRenderedFeatures(event.point, { layers: glyphLayers }).length > 0
      ) {
        return;
      }
      const candidates = map
        .queryRenderedFeatures(trackHitBox(event.point), { layers: [OBJECT_TRACK_LAYER_ID] })
        .flatMap((feature) => {
          const trackId = feature.properties?.track_id;
          if (typeof trackId !== "string" || trackId.length === 0) {
            return [];
          }
          if (feature.geometry.type !== "LineString") {
            return [];
          }
          return [{ trackId, coordinates: feature.geometry.coordinates }];
        });
      const id = nearestTrackAtPoint(candidates, event.point, (position) =>
        map.project(position as [number, number]),
      );
      if (id !== null) {
        selectOnMap({ kind: "track", id });
      }
    };
    map.on("click", onClick);
    return () => {
      map.off("click", onClick);
    };
  }, [map, selectOnMap]);

  // Consume the force-focus signal (REQ-UI-172): fit the track's extent.
  // Keyed on ``nonce`` so a repeat "Select on Map" on the same row re-fits;
  // the target and the record are read at fire time.
  useEffect(() => {
    if (focusSignal.nonce === 0) {
      return;
    }
    const target = focusSignal.target;
    if (target === null || target.kind !== "track") {
      return;
    }
    const record = useObjectTrackStore.getState().byId[target.id];
    if (record === undefined) {
      return; // Cold selection — no extent known here.
    }
    const bounds = trackFocusBounds(record);
    if (bounds === null) {
      return; // Unpositioned track — the panel disables the item anyway.
    }
    if (bounds.sw[0] === bounds.ne[0] && bounds.sw[1] === bounds.ne[1]) {
      map.easeTo({ center: bounds.sw, duration: TRACK_FOCUS_DURATION_MS });
      return;
    }
    const box: LngLatBoundsLike = [bounds.sw, bounds.ne];
    map.fitBounds(box, {
      padding: TRACK_FOCUS_PADDING_PX,
      maxZoom: TRACK_FOCUS_MAX_ZOOM,
      duration: TRACK_FOCUS_DURATION_MS,
    });
    // ``focusSignal.target`` is deliberately not a dependency — only a fresh
    // request (a nonce bump) should move the camera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, focusSignal.nonce]);

  return null;
}
